import { guardNumber } from "../numbers";
import type { CalculationFormServiceItem } from "../schemas";
import { calculateService } from "../services";
import type { NullableFields } from "../types";

/**
 * Calculates the total price for a calculation item of any type, dispatching to the
 * block rollup for `type: "block"` items or the plain unit-based calculation otherwise
 * (which itself sums named positions when the item holds any, see `calculateService`).
 *
 * @param item - The item to calculate the total for.
 * @param allItems - The full flat list of items on the calculation, needed to resolve a block's children.
 * @returns The item's total price.
 */
export function calculateServiceCalculationItemTotal(
	item: NullableFields<CalculationFormServiceItem>,
	allItems: NullableFields<CalculationFormServiceItem>[],
): number {
	if (item.type === "block") return calculateBlockTotal(item, allItems);
	return calculateService(item);
}

/**
 * Calculates the rolled-up total for a block item: the sum of its direct children's
 * totals, multiplied by the block's quantity.
 *
 * @param item - The block item to calculate the total for.
 * @param allItems - The full flat list of items the block's children are looked up from.
 * @returns The block's rolled-up total price.
 */
export function calculateBlockTotal(
	item: NullableFields<CalculationFormServiceItem>,
	allItems: NullableFields<CalculationFormServiceItem>[],
): number {
	const children = allItems.filter((child) => child.parentServiceCalculationItemId === item.id);
	const childrenTotal = children.reduce((sum, child) => {
		if (child.isEventualPosition) return sum;
		return sum + guardNumber(calculateServiceCalculationItemTotal(child, allItems));
	}, 0);
	return childrenTotal * (guardNumber(item.quantity) || 1);
}
